import { HugeiconsIcon } from "@hugeicons/react";
import {
  Call02Icon,
  Cancel01Icon,
  User02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Avatar } from "shared/components/Avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { cx } from "shared/utils";
import {
  UNKNOWN_CALLER_LABEL,
  isCallerUnknown,
  transferSourceLabel
} from "shared/utils/transferred-call";
import { formatPhoneDisplay } from "../views/phone-numbers/lib/format";
import { incomingCallerLabel } from "./callDisplay";
import { useSoftphone } from "./SoftphoneProvider";
import { SOFTPHONE_OVERLAY_Z_CLASS } from "./stacking";
import {
  SEND_TO_AI_TOOLTIP,
  describeIncomingOutcome,
  useTeamFirstCountdown
} from "./teamFirst";
import {
  AiAgentAvatar,
  OutcomeIcon,
  TeamFirstProgressBar
} from "./TeamFirstParts";
import { useDraggableOverlay, type DragOffset } from "./useDraggableOverlay";

const ACTIVE_AVATAR_COLOR = "#29B6F6";
// Stage colors are partner-authored in CRM settings, so treat anything that is
// not a plain hex value as missing rather than feeding it to the style prop.
const HEX_COLOR = /^#[0-9a-f]{3,8}$/i;
const FALLBACK_STAGE_COLOR = "#8b8d97"; // content-300

interface IncomingCallScreenProps {
  // Collapse the popup to the incoming-call banner.
  onMinimize: () => void;
  // Drag position, owned by the overlay so it survives minimize/expand and the
  // hand-off to the in-call card once the call is answered (CRM-1318).
  dragOffset: DragOffset;
  onDragOffsetChange: (offset: DragOffset) => void;
}

/**
 * Incoming-call popup (CRM-905, Figma 23471:85906). Floats bottom-right like
 * the in-call card: caller avatar + number, matched lead name and the
 * community that lead belongs to (CRM-1655), the lead's status (CRM-1221), the
 * dialed line, "Incoming call" and the Start call / Silence pair.
 * Silence only takes THIS browser out of the call — colleagues keep ringing
 * (Jon, 2026-08-24). The X collapses to the banner. Drag it anywhere on
 * screen to uncover what it sits on top of (CRM-1318): a ring for a
 * colleague's line or a lead nobody is picking up
 * must not keep the Inbox Send button covered until it stops.
 *
 * A call the partner's AI leasing agent escalated (CRM-1393) shows the ORIGINAL
 * caller — the api already resolved them into the caller number — plus which
 * agent line they reached; when the api could not tell who was on the line the
 * headline says so instead of printing the park's own number as if it were a
 * person.
 */
export function IncomingCallScreen({
  onMinimize,
  dragOffset,
  onDragOffsetChange
}: IncomingCallScreenProps) {
  const {
    incomingCall,
    incomingOutcome,
    acceptIncoming,
    silenceIncoming,
    sendIncomingToAi
  } = useSoftphone();
  const countdown = useTeamFirstCountdown(incomingCall);

  const { nodeRef, dragClassName, style, dragProps } = useDraggableOverlay({
    offset: dragOffset,
    onOffsetChange: onDragOffsetChange
  });

  if (!incomingCall) return null;

  const outcomeCopy = describeIncomingOutcome(incomingOutcome);
  const ringing = !incomingOutcome;

  const callerUnknown = isCallerUnknown(
    incomingCall.fromNumber,
    incomingCall.transferredFromNumber
  );
  const displayNumber = callerUnknown
    ? UNKNOWN_CALLER_LABEL
    : formatPhoneDisplay(incomingCall.fromNumber);
  const displayName = incomingCall.leadName?.trim();
  const callerLabel = incomingCallerLabel(incomingCall);
  const displayStage = incomingCall.leadStage?.trim();
  const transferSource = transferSourceLabel(
    incomingCall.transferredFromNumber,
    incomingCall.transferredFromName,
    incomingCall.transferredByName
  );

  return (
    <div
      ref={nodeRef}
      role="dialog"
      aria-label="Incoming call"
      style={style}
      {...dragProps}
      className={cx(
        "fixed bottom-4 right-4 w-[400px] max-w-[calc(100vw-2rem)] rounded-2xl bg-white px-6 pb-7 pt-5 shadow-2xl ring-1 ring-alpha-100",
        dragClassName,
        SOFTPHONE_OVERLAY_Z_CLASS
      )}
    >
      <div className="flex h-8 items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-medium text-content-400">
          <span
            aria-hidden="true"
            className="size-2 rounded-full bg-success-500"
          />
          Incoming call
        </span>
        {ringing ? (
          <button
            type="button"
            aria-label="Minimize"
            onClick={onMinimize}
            className="-mr-2 grid size-8 place-items-center rounded-full text-content-300 transition hover:bg-alpha-40 hover:text-content-1000"
          >
            <HugeiconsIcon icon={Cancel01Icon} size={20} />
          </button>
        ) : null}
      </div>

      <div className="mt-6 flex flex-col items-center text-center">
        {/* Initials only for a matched lead; an unknown caller gets the
            silhouette (Jon, 2026-08-24) — the number must not go into `name`
            or it turns into "+(" initials. */}
        <Avatar
          name={displayName}
          size={88}
          className="text-3xl font-semibold"
          fallbackText={<HugeiconsIcon icon={User02Icon} size={32} />}
          fallbackBackgroundColor={ACTIVE_AVATAR_COLOR}
        />

        <p className="mt-5 text-base font-semibold text-content-1000">
          {displayNumber}
        </p>
        {callerLabel ? (
          <p className="mt-1 text-sm text-content-400">{callerLabel}</p>
        ) : null}
        <CallMeta
          stage={displayStage}
          stageColor={incomingCall.leadStageColor}
          line={dialedLineLabel(incomingCall.numberLabel)}
        />
        {transferSource ? (
          <p className="mt-1 text-[13px] text-content-300">
            Transferred from {transferSource}
          </p>
        ) : null}
        {incomingCall.transferReturn ? (
          // CRM-1747: a Transfer now nobody answered, coming back to its sender
          // after this browser lost track of it (e.g. the card was closed).
          <p className="mt-1 text-[13px] text-content-300">
            {incomingCall.transferredToName || "Your transfer"} didn&apos;t
            answer — the call came back to you
          </p>
        ) : null}

        {outcomeCopy ? (
          <div className="mt-8 flex min-h-[134px] flex-col items-center gap-1">
            <OutcomeIcon tone={outcomeCopy.tone} />
            <p className="mt-1 max-w-[320px] text-sm font-bold text-content-1000">
              {outcomeCopy.message}
            </p>
            <p className="text-[13px] leading-4 text-content-300">
              {outcomeCopy.detail}
            </p>
          </div>
        ) : (
          <>
            <div className="mt-8 flex min-h-12 w-full flex-col items-center justify-center">
              {countdown ? (
                <>
                  <TeamFirstProgressBar
                    total={countdown.total}
                    remaining={countdown.remaining}
                  />
                  <p className="mt-[11px] text-[13px] font-medium leading-4 text-content-300">
                    AI agent picks up in {countdown.secondsLeft}s…
                  </p>
                </>
              ) : null}
            </div>

            <div
              className={cx(
                "mt-6 flex items-start justify-center",
                countdown ? "gap-8" : "gap-10",
                !ringing && "invisible"
              )}
            >
              {countdown ? (
                <Tooltip placement="top" delay={{ open: 300 }}>
                  <TooltipTrigger asChild>
                    <IncomingControlButton
                      label="Send to AI"
                      ariaLabel="Send to AI agent"
                      onClick={sendIncomingToAi}
                      circle={<AiAgentAvatar />}
                    />
                  </TooltipTrigger>
                  <TooltipContent className="z-[1000] max-w-[260px] rounded-lg bg-alpha-800 px-3 py-2 text-sm text-white">
                    {SEND_TO_AI_TOOLTIP}
                  </TooltipContent>
                </Tooltip>
              ) : null}
              <IncomingControlButton
                label="Start call"
                onClick={acceptIncoming}
                circle={
                  <ControlCircle
                    className="bg-success-500 text-white hover:bg-success-600"
                    icon={Call02Icon}
                  />
                }
              />
              <IncomingControlButton
                label="Silence"
                onClick={silenceIncoming}
                circle={
                  <ControlCircle
                    className="bg-danger-500 text-white hover:bg-danger-600"
                    icon={Cancel01Icon}
                  />
                }
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// The dialed line comes as a partner label or the bare E.164 number.
const dialedLineLabel = (label?: string | null) => {
  const value = label?.trim();
  if (!value) return undefined;
  return /^\+?\d{10,15}$/.test(value) ? formatPhoneDisplay(value) : value;
};

/**
 * One muted line under the caller: the matched lead's pipeline stage (its own
 * color as a dot, CRM-1221 — omitted when the matching leads disagree) and the
 * line they dialed.
 */
function CallMeta({
  stage,
  stageColor,
  line
}: {
  stage?: string;
  stageColor?: string;
  line?: string;
}) {
  if (!stage && !line) return null;
  const dotColor =
    stageColor && HEX_COLOR.test(stageColor)
      ? stageColor
      : FALLBACK_STAGE_COLOR;

  return (
    <p className="mt-2 flex max-w-full flex-wrap items-center justify-center gap-x-1.5 text-sm text-content-300">
      {stage ? (
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <span
            aria-hidden="true"
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: dotColor }}
          />
          <span className="sr-only">Lead status:</span>
          <span className="truncate">{stage}</span>
        </span>
      ) : null}
      {stage && line ? <span aria-hidden="true">·</span> : null}
      {line ? <span className="truncate">To: {line}</span> : null}
    </p>
  );
}

const IncomingControlButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> & {
    label: string;
    ariaLabel?: string;
    onClick: () => void;
    circle: ReactNode;
  }
>(function IncomingControlButton(
  { label, ariaLabel, onClick, circle, ...rest },
  ref
) {
  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className="flex min-w-12 flex-col items-center gap-2"
    >
      {circle}
      <span className="text-sm text-content-1000">{label}</span>
    </button>
  );
});

function ControlCircle({
  className,
  icon
}: {
  className: string;
  icon: typeof Call02Icon;
}) {
  return (
    <span
      className={cx(
        "grid size-12 place-items-center rounded-full transition",
        className
      )}
    >
      <HugeiconsIcon icon={icon} size={22} />
    </span>
  );
}
